import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api';
import {
  Smartphone,
  Lock,
  Trash2,
  Upload,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Shield,
} from 'lucide-react';

interface AndroidDevice {
  id: string;
  model?: string;
  os_version?: string;
  owner_email?: string;
  last_seen?: string;
  status?: 'online' | 'offline' | 'warning';
  compliance?: 'compliant' | 'non-compliant' | 'warning';
}

const AndroidManagement: React.FC = () => {
  const [devices, setDevices] = useState<AndroidDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedDeviceDetails, setSelectedDeviceDetails] = useState<AndroidDevice | null>(null);
  const [showDeviceDetails, setShowDeviceDetails] = useState(false);
  const [deviceCompliance, setDeviceCompliance] = useState<any>(null);

  useEffect(() => {
    loadDevices();
    // Refresh devices every 30 seconds
    const interval = setInterval(loadDevices, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Tenant-scoped core registry only (actions go through /mobile-mdm proxies)
      let androidDevices: AndroidDevice[] = [];
      const coreDevices = await apiService.tenantListDevices('android');
      if (Array.isArray(coreDevices) && coreDevices.length > 0) {
        androidDevices = coreDevices.map((d: any) => ({
          id: d.platform_device_id || d.device_id || d.id,
          model: d.model || d.name || 'Unknown',
          os_version: d.os_version || d.os || 'Android',
          owner_email: d.owner_email || 'N/A',
          last_seen: d.last_seen,
          status: getDeviceStatus(d.last_seen),
          compliance: d.compliance_status === 'non_compliant' ? 'non-compliant' : 'compliant',
          _coreDeviceId: d.device_id,
        }));
      } else {
        // Empty tenant registry — try mobile-mdm list (same tenant scope)
        const mobile = await apiService.listMobileMdmDevices('android');
        androidDevices = (Array.isArray(mobile) ? mobile : []).map((d: any) => ({
          id: d.platform_device_id || d.device_id || d.id,
          model: d.model || d.name || 'Unknown',
          os_version: d.os_version || d.os || 'Android',
          owner_email: d.owner_email || 'N/A',
          last_seen: d.last_seen,
          status: getDeviceStatus(d.last_seen),
          compliance: d.compliance_status === 'non_compliant' ? 'non-compliant' : 'compliant',
          _coreDeviceId: d.device_id,
        }));
      }

      setDevices(androidDevices);
    } catch (err: any) {
      console.error('Error loading Android devices:', err);
      setError(err.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceStatus = (lastSeen?: string): 'online' | 'offline' | 'warning' => {
    if (!lastSeen) return 'offline';
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const minutesAgo = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    if (minutesAgo < 5) return 'online';
    if (minutesAgo < 30) return 'warning';
    return 'offline';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <span className="text-green-500">🟢</span>;
      case 'offline':
        return <span className="text-red-500">🔴</span>;
      case 'warning':
        return <span className="text-yellow-500">🟡</span>;
      default:
        return <span className="text-gray-500">⚪</span>;
    }
  };

  // Filter devices based on search and status
  const filteredDevices = useMemo(() => {
    let filtered = devices;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(device =>
        device.id.toLowerCase().includes(query) ||
        device.model?.toLowerCase().includes(query) ||
        device.owner_email?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(device => device.status === statusFilter);
    }

    return filtered;
  }, [devices, searchQuery, statusFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = devices.length;
    const compliant = devices.filter(d => d.compliance === 'compliant').length;
    const alerts = devices.filter(d => d.status === 'offline' || d.compliance !== 'compliant').length;
    const compliantPercent = total > 0 ? Math.round((compliant / total) * 100) : 0;

    return { total, compliant, compliantPercent, alerts };
  }, [devices]);

  const handleEnrollDevice = () => {
    setShowEnrollModal(true);
  };

  const handleLockDevices = async () => {
    if (selectedDevices.length === 0) {
      window.alert('Please select at least one device');
      return;
    }
    if (!window.confirm(`Lock ${selectedDevices.length} device(s)?`)) return;

    try {
      for (const deviceId of selectedDevices) {
        await apiService.lockAndroidDevice(deviceId);
      }
      window.alert(`Lock command sent to ${selectedDevices.length} device(s)`);
      setSelectedDevices([]);
    } catch (err: any) {
      window.alert(`Failed to lock devices: ${err.message}`);
    }
  };

  const handleWipeDevices = async () => {
    if (selectedDevices.length === 0) {
      window.alert('Please select at least one device');
      return;
    }
    const confirmMessage = `WIPE ${selectedDevices.length} device(s)?\n\nWARNING: This will ERASE ALL DATA on the device(s)!\n\nThis action CANNOT be undone!\n\nType "WIPE" to confirm:`;
    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'WIPE') {
      return;
    }

    try {
      for (const deviceId of selectedDevices) {
        await apiService.wipeAndroidDevice(deviceId);
      }
      window.alert(`Wipe command sent to ${selectedDevices.length} device(s). Devices will be wiped on next check-in.`);
      setSelectedDevices([]);
    } catch (err: any) {
      window.alert(`Failed to wipe devices: ${err.message}`);
    }
  };

  const handlePushProfile = async () => {
    if (selectedDevices.length === 0) {
      window.alert('Please select at least one device');
      return;
    }

    // Get policies for selection
    try {
      const policies = await apiService.getBuntuMDMPolicies();
      if (policies.length === 0) {
        window.alert('No policies available. Please create a policy first.');
        return;
      }

      // For now, use first policy or show selection modal
      const policyId = policies[0].id;
      for (const deviceId of selectedDevices) {
        await apiService.assignBuntuMDMPolicy(deviceId, policyId);
      }
      window.alert(`Profile pushed to ${selectedDevices.length} device(s)`);
      loadDevices();
    } catch (err: any) {
      window.alert(`Failed to push profile: ${err.message}`);
    }
  };

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map(d => d.id));
    }
  };

  const loadDeviceCompliance = async (deviceId: string) => {
    try {
      const compliance = await apiService.getAndroidDeviceCompliance(deviceId);
      setDeviceCompliance(compliance);
    } catch (err) {
      console.error('Failed to load compliance:', err);
      setDeviceCompliance(null);
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading Android devices...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Android Fleet Management</h1>
        <p className="text-gray-600 mt-1">Manage and monitor your Android device fleet</p>
      </div>

      {/* Devices only (Windows-style) */}
          {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Devices</p>
              <p className="text-3xl font-bold mt-1">{stats.total}</p>
            </div>
            <Smartphone className="w-12 h-12 text-blue-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliant</p>
              <p className="text-3xl font-bold mt-1">{stats.compliantPercent}%</p>
            </div>
            <Shield className="w-12 h-12 text-green-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Alerts</p>
              <p className="text-3xl font-bold mt-1">{stats.alerts}</p>
            </div>
            <AlertCircle className="w-12 h-12 text-red-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleEnrollDevice}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Enroll Device
          </button>
          <button
            onClick={handleLockDevices}
            disabled={selectedDevices.length === 0}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
            title={selectedDevices.length === 0 ? 'Select devices first' : 'Lock selected devices'}
          >
            <Lock className="w-4 h-4 mr-2" />
            Lock
          </button>
          <button
            onClick={handleWipeDevices}
            disabled={selectedDevices.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium shadow-sm"
            title={selectedDevices.length === 0 ? 'Select devices first' : 'Wipe selected devices (WARNING: This will erase all data!)'}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Wipe
          </button>
          <button
            onClick={handlePushProfile}
            disabled={selectedDevices.length === 0}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
            title={selectedDevices.length === 0 ? 'Select devices first' : 'Push policy profile to selected devices'}
          >
            <Upload className="w-4 h-4 mr-2" />
            Push Profile
          </button>
          {selectedDevices.length > 0 && (
            <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded text-sm font-medium">
              {selectedDevices.length} selected
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={loadDevices}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search Devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="warning">Warning</option>
            </select>
          </div>
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">OS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredDevices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    {devices.length === 0 ? (
                      <div>
                        <Smartphone className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium mb-2">No Android devices enrolled</p>
                        <p className="text-sm mb-4">Use an enrollment invite + the Buntu Android agent APK</p>
                        <button
                          onClick={handleEnrollDevice}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          How to enroll
                        </button>
                      </div>
                    ) : (
                      'No devices match your search criteria'
                    )}
                  </td>
                </tr>
              ) : (
                filteredDevices.map((device) => (
                  <tr
                    key={device.id}
                    className={`hover:bg-gray-50 ${selectedDevices.includes(device.id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => toggleDeviceSelection(device.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const isOnline = device.last_seen && (new Date().getTime() - new Date(device.last_seen).getTime() < 5 * 60 * 1000);
                        return (
                          <div className="flex items-center">
                            <div className={`w-2.5 h-2.5 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <span className="text-sm capitalize">{isOnline ? 'online' : 'offline'}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedDeviceDetails(device);
                          loadDeviceCompliance(device.id);
                          setShowDeviceDetails(true);
                        }}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {device.id}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-1" />
                        {device.owner_email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{device.model || 'Unknown'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        {device.os_version || 'Android'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Device Details Modal */}
      {showDeviceDetails && selectedDeviceDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold">Device Details</h3>
              <button
                onClick={() => {
                  setShowDeviceDetails(false);
                  setSelectedDeviceDetails(null);
                  setDeviceCompliance(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Device Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Device Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Device ID</p>
                    <p className="font-mono text-sm">{selectedDeviceDetails.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Model</p>
                    <p className="text-sm">{selectedDeviceDetails.model || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">OS Version</p>
                    <p className="text-sm">{selectedDeviceDetails.os_version || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Owner</p>
                    <p className="text-sm">{selectedDeviceDetails.owner_email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <div className="flex items-center">
                      {getStatusIcon(selectedDeviceDetails.status || 'offline')}
                      <span className="ml-2 capitalize">{selectedDeviceDetails.status || 'offline'}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Seen</p>
                    <p className="text-sm">
                      {selectedDeviceDetails.last_seen
                        ? new Date(selectedDeviceDetails.last_seen).toLocaleString()
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compliance Status */}
              {deviceCompliance && (
                <div className={`p-4 rounded-lg border-2 ${
                  deviceCompliance.is_compliant
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Compliance Status</h4>
                    {deviceCompliance.is_compliant ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <p className={`text-sm font-medium mb-2 ${
                    deviceCompliance.is_compliant ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {deviceCompliance.is_compliant ? 'Compliant' : 'Non-Compliant'}
                  </p>
                  {deviceCompliance.issues && deviceCompliance.issues.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">Issues:</p>
                      <ul className="list-disc list-inside text-sm text-gray-600">
                        {deviceCompliance.issues.map((issue: string, idx: number) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Last checked: {new Date(deviceCompliance.last_check).toLocaleString()}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={async () => {
                    try {
                      await apiService.syncAndroidPolicy(selectedDeviceDetails.id);
                      window.alert('Policy sync command sent');
                      loadDevices();
                    } catch (err: any) {
                      window.alert(`Failed to sync policy: ${err.message}`);
                    }
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Sync Policy
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Lock this device?')) return;
                    try {
                      await apiService.lockAndroidDevice(selectedDeviceDetails.id);
                      window.alert('Lock command sent');
                    } catch (err: any) {
                      window.alert(`Failed to lock device: ${err.message}`);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                >
                  Lock Device
                </button>
                <button
                  onClick={async () => {
                    const confirm = window.prompt('Type "WIPE" to confirm device wipe:');
                    if (confirm !== 'WIPE') return;
                    try {
                      await apiService.wipeAndroidDevice(selectedDeviceDetails.id);
                      window.alert('Wipe command sent. Device will be wiped on next check-in.');
                    } catch (err: any) {
                      window.alert(`Failed to wipe device: ${err.message}`);
                    }
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Wipe Device
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Device Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-2">Enroll Android Device</h3>
            <p className="text-gray-600 mb-4 text-sm">
              There is no hosted APK download yet. Enrollment needs the Buntu agent built from source,
              plus a running Buntu MDM backend and a Hub invite.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 mb-4">
              <li>
                In <strong>Client Admin → Enrollment Invites</strong>, create an invite for platform{' '}
                <code>android</code> (mode e.g. <code>WORK_PROFILE</code>). Copy the token / URL.
              </li>
              <li>
                Build the agent APK from <code>buntu_mdm/agent-android</code> (see BUILD.md). Set the
                server URL to your Buntu MDM host (not ClickHouse on :9000).
              </li>
              <li>Sideload the APK onto the phone (enable Install unknown apps).</li>
              <li>
                Open <strong>Buntu Agent</strong>, enter a Device ID and owner email, then enroll.
                (Current MVP still posts to Buntu <code>/api/enroll</code>; Hub registry sync picks it up
                for Nutrinium.)
              </li>
              <li>Enable Device Admin and complete Work Profile setup when prompted.</li>
              <li>Refresh this page — the device should appear in the tenant Android list.</li>
            </ol>
            <div className="bg-amber-50 border border-amber-200 p-3 rounded mb-3 text-sm text-amber-900">
              <strong>Why you saw @datail.in:</strong> that was leftover placeholder copy. The agent
              source still validates <code>@datail.in</code> emails until we ship an invite-token build.
              For a test build you can temporarily relax that check in{' '}
              <code>MainActivity.java</code>.
            </div>
            <div className="bg-blue-50 p-3 rounded mb-4 text-sm text-blue-900">
              <strong>APK path:</strong> open <code>/opt/uem/buntu_mdm/agent-android</code> in Android
              Studio → Build → Build APK(s). Output:{' '}
              <code>app/build/outputs/apk/debug/app-debug.apk</code>
            </div>
            <button
              onClick={() => setShowEnrollModal(false)}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Got it
            </button>
          </div>
        </div>
      )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}
    </div>
  );
};

export default AndroidManagement;
